import { useMemo } from 'react'
import { Box, IconButton, Stack, TextField, Typography } from '@mui/material'
import { Trash2 } from 'lucide-react'
import { useOnboarding } from '../../context/OnboardingContext'
import { licenseNameInputValue, type LicenseRow } from '../../types/onboarding'
import { LicenseStatesIntro } from '../licenseSteps/LicenseStatesIntro'
import { LicenseAddMoreButton } from '../licenseSteps/LicenseAddMoreButton'
import { LicenseImportButton } from '../licenseSteps/LicenseImportButton'
import { createEmptyBusinessLicense } from '../licenseSteps/licenseRowFactory'
import {
  LicenseTableBody,
  LicenseTableHead,
  LicenseTableRow,
  LicenseTableShell,
  LicenseTableTd,
  LicenseTableTh,
  licenseCellInputSx,
} from '../licenseSteps/LicenseTable'

const cellMonoSx = {
  ...(licenseCellInputSx as object),
  '& .MuiOutlinedInput-input': {
    px: 0.5,
    py: 0.25,
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
    fontSize: 13,
    '&::placeholder': { color: '#94a3b8', opacity: 1 },
  },
}

export function StepBusinessLicenses() {
  const { state, dispatch } = useOnboarding()

  const businessRows = useMemo(
    () => state.licenses.filter((l) => l.entity === 'BusinessEntity'),
    [state.licenses],
  )

  const patch = (id: string, patch: Partial<LicenseRow>) => {
    dispatch({ type: 'UPDATE_LICENSE', id, patch })
  }

  return (
    <Stack spacing={3}>
      <LicenseStatesIntro entityLabel="entities" />

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
        }}
      >
        <Typography component="h3" sx={{ fontSize: '1.5rem', fontWeight: 700, color: '#0f172a' }}>
          Business Entity Licenses
        </Typography>
        <LicenseImportButton />
      </Box>

      <LicenseTableShell minWidth="900px">
        <LicenseTableHead>
          <LicenseTableTh sx={{ width: '28%' }}>Business Entity Name</LicenseTableTh>
          <LicenseTableTh sx={{ width: '22%' }}>Resident SL License State</LicenseTableTh>
          <LicenseTableTh sx={{ width: '18%' }}>License Number</LicenseTableTh>
          <LicenseTableTh sx={{ width: '16%' }}>NPN</LicenseTableTh>
          <LicenseTableTh sx={{ width: '16%' }}>FEIN</LicenseTableTh>
          <LicenseTableTh sx={{ width: '60px', textAlign: 'center' }}>Actions</LicenseTableTh>
        </LicenseTableHead>
        <LicenseTableBody>
          {businessRows.length === 0 ? (
            <LicenseTableRow>
              <LicenseTableTd colSpan={6} sx={{ color: '#64748b' }}>
                <Typography sx={{ display: 'block', py: 2, textAlign: 'center', fontSize: 14 }}>
                  No business entities on file. They will appear here after the license report runs.
                </Typography>
              </LicenseTableTd>
            </LicenseTableRow>
          ) : (
            businessRows.map((row, i) => (
              <LicenseTableRow key={row.licenseeId} striped={i % 2 === 0}>
                <LicenseTableTd>
                  <TextField
                    size="small"
                    sx={licenseCellInputSx}
                    value={licenseNameInputValue(row)}
                    onChange={(e) =>
                      patch(row.licenseeId, {
                        licenseeFirstName: '',
                        licenseeLastName: e.target.value,
                      })
                    }
                    placeholder="e.g. Acme Surplus LLC"
                    inputProps={{ 'aria-label': 'Business entity name' }}
                  />
                </LicenseTableTd>
                <LicenseTableTd>
                  <TextField
                    size="small"
                    sx={{
                      ...(licenseCellInputSx as object),
                      '& .MuiOutlinedInput-input': { textTransform: 'uppercase' },
                    }}
                    value={row.licenseState.toUpperCase()}
                    inputProps={{ maxLength: 2, 'aria-label': 'Resident SL license state' }}
                    onChange={(e) => {
                      const code = e.target.value.trim().slice(0, 2).toUpperCase()
                      patch(row.licenseeId, { licenseState: code })
                    }}
                    placeholder="e.g. CA"
                  />
                </LicenseTableTd>
                <LicenseTableTd>
                  <TextField
                    size="small"
                    sx={cellMonoSx}
                    value={row.licenseNumber}
                    onChange={(e) => patch(row.licenseeId, { licenseNumber: e.target.value })}
                    inputProps={{ 'aria-label': 'License number' }}
                  />
                </LicenseTableTd>
                <LicenseTableTd>
                  <TextField
                    size="small"
                    sx={cellMonoSx}
                    value={row.npn}
                    onChange={(e) => patch(row.licenseeId, { npn: e.target.value })}
                    inputProps={{ 'aria-label': 'NPN' }}
                  />
                </LicenseTableTd>
                <LicenseTableTd>
                  <TextField
                    size="small"
                    sx={cellMonoSx}
                    value={row.fein ?? ''}
                    onChange={(e) => patch(row.licenseeId, { fein: e.target.value })}
                    inputProps={{ 'aria-label': 'FEIN' }}
                  />
                </LicenseTableTd>
                <LicenseTableTd sx={{ textAlign: 'center' }}>
                  <IconButton
                    size="small"
                    onClick={() => dispatch({ type: 'REMOVE_LICENSE', licenseeId: row.licenseeId })}
                    sx={{
                      color: '#64748b',
                      '&:hover': { color: '#dc2626', backgroundColor: '#fee2e2' },
                    }}
                    aria-label={`Delete ${licenseNameInputValue(row)}`}
                  >
                    <Trash2 size={16} />
                  </IconButton>
                </LicenseTableTd>
              </LicenseTableRow>
            ))
          )}
        </LicenseTableBody>
      </LicenseTableShell>

      <LicenseAddMoreButton createRow={createEmptyBusinessLicense} />
    </Stack>
  )
}
